import { Artifact, ArtifactSearchResult, CreateArtifactData, UpdateArtifactData } from '@/types/artifact';
import { StorageError, StorageErrorCode, StorageInfo, StorageService } from '@/types/storage';
import { v4 as uuidv4 } from 'uuid';
import { htmlParser } from '../htmlParser';

const LOCAL_STORAGE_KEY = 'artifact-sandbox';

export class LocalStorageService implements StorageService {
  private artifacts: Artifact[] = [];

  constructor() {
    this.loadFromLocalStorage();
  }

  private loadFromLocalStorage() {
    try {
      const data = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (data) {
        this.artifacts = JSON.parse(data);
      }
    } catch (error) {
      console.error('Failed to load from LocalStorage:', error);
      this.artifacts = [];
    }
  }

  private saveToLocalStorage() {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.artifacts));
    } catch (error) {
      console.error('Failed to save to LocalStorage:', error);
      throw new StorageError(StorageErrorCode.QUOTA_EXCEEDED, 'LocalStorage quota exceeded.');
    }
  }

  async createArtifact(data: CreateArtifactData): Promise<Artifact> {
    const now = new Date();
    const metadata = htmlParser.extractMetadata(data.htmlContent);

    const artifact: Artifact = {
      id: uuidv4(),
      title: data.title.trim() || 'Untitled Artifact',
      htmlContent: data.htmlContent,
      createdAt: now,
      updatedAt: now,
      metadata,
    };

    this.artifacts.push(artifact);
    this.saveToLocalStorage();
    return artifact;
  }

  async getArtifact(id: string): Promise<Artifact | null> {
    return this.artifacts.find(a => a.id === id) || null;
  }

  async getAllArtifacts(): Promise<Artifact[]> {
    return [...this.artifacts].sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
  }

  async updateArtifact(id: string, updates: UpdateArtifactData): Promise<Artifact> {
    const artifactIndex = this.artifacts.findIndex(a => a.id === id);
    if (artifactIndex === -1) {
      throw new StorageError(StorageErrorCode.NOT_FOUND, 'Artifact not found');
    }

    const existingArtifact = this.artifacts[artifactIndex];
    const updatedArtifact = {
      ...existingArtifact,
      ...updates,
      updatedAt: new Date(),
    };

    if (updates.htmlContent) {
      updatedArtifact.metadata = htmlParser.extractMetadata(updates.htmlContent);
    }

    this.artifacts[artifactIndex] = updatedArtifact;
    this.saveToLocalStorage();
    return updatedArtifact;
  }

  async deleteArtifact(id: string): Promise<boolean> {
    const initialLength = this.artifacts.length;
    this.artifacts = this.artifacts.filter(a => a.id !== id);
    if (this.artifacts.length < initialLength) {
      this.saveToLocalStorage();
      return true;
    }
    return false;
  }

  async importArtifacts(artifacts: Artifact[]): Promise<void> {
    // Basic deduplication
    const newArtifacts = artifacts.filter(newArtifact =>
      !this.artifacts.some(existing => existing.id === newArtifact.id)
    );
    this.artifacts.push(...newArtifacts);
    this.saveToLocalStorage();
  }

  async exportArtifacts(): Promise<Artifact[]> {
    return JSON.parse(JSON.stringify(this.artifacts));
  }

  async searchArtifacts(query: string): Promise<ArtifactSearchResult[]> {
    const lowerCaseQuery = query.toLowerCase();
    const results = this.artifacts
      .filter(a =>
        a.title.toLowerCase().includes(lowerCaseQuery)
      )
      .map(artifact => ({
        artifact,
        score: 1,
        matches: [],
      }));
    return results;
  }


  async clearAll(): Promise<void> {
    this.artifacts = [];
    this.saveToLocalStorage();
  }

  async getStorageInfo(): Promise<StorageInfo> {
    const totalSize = this.artifacts.reduce((sum, a) => sum + a.metadata.size, 0);
    return {
      totalArtifacts: this.artifacts.length,
      totalSize,
      storageQuota: 5 * 1024 * 1024, // Approx 5MB for LocalStorage
      storageUsed: totalSize,
      availableSpace: 5 * 1024 * 1024 - totalSize,
    };
  }
} 